import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type RankType = { id: number; rank: number };

export const initState = {
  q1: 0,
  q2: 0,
  q3: [] as number[],
  q4: [] as number[],
  q5: [] as number[],
  q6: 3,
  q7: 0,
};

export type QKey = keyof typeof initState;

const defaultValues = {
  values: initState,
  isComplete: false,
  setComplete: (): void => undefined,
  setValue: (key: QKey, value: number): void => undefined,
  retake: (str?: string): void => undefined,
  showResult: false,
  goToResult: (): void => undefined,
};

export const AnswerContext = React.createContext(defaultValues);

export const AnswerProvider: React.FC<{ data: typeof initState }> = ({
  data,
  children,
}) => {
  const [context, setContext] = useState(data);
  const [isComplete, setIsComplete] = useState(false);
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    const keys = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'] as const;
    const obj: any = {};
    keys.forEach((k) => {
      const item = sessionStorage.getItem(k);
      if (item) {
        obj[k] = JSON.parse(item);
      } else {
        obj[k] = initState[k];
      }
    });
    setContext(obj);
  }, []);

  useEffect(() => {
    const status = sessionStorage.getItem('complete');
    if (status === 'true') {
      setIsComplete(true);
    }
  }, []);

  const setValue = useCallback(
    (key: QKey, value: number | number[] | RankType[]) => {
      if (
        (key === 'q3' || key === 'q4' || key === 'q5') &&
        typeof value === 'number'
      ) {
        setContext((values) => {
          let current = values[key];
          const item = current.find((x) => x === value);
          if (item) {
            current = current.filter((x) => x !== value);
          } else {
            current = [...current, value];
          }
          return { ...values, [key]: current };
        });
        return;
      }
      sessionStorage.setItem(key, JSON.stringify(value));
      setContext((x) => ({ ...x, [key]: value }));
    },
    [],
  );

  const setComplete = useCallback(() => {
    setIsComplete(true);
    sessionStorage.setItem('complete', 'true');
  }, []);

  const goToResult = useCallback(() => {
    setShowResult(true);
  }, []);

  const retake = useCallback((step = '0') => {
    setContext(initState);
    sessionStorage.setItem('showInsight', 'false');
    sessionStorage.removeItem('complete');
    sessionStorage.setItem('step', step);
    setIsComplete(false);
    setShowResult(false);
  }, []);

  const value = useMemo(
    () => ({
      isComplete,
      setComplete,
      values: context,
      setValue,
      retake,
      goToResult,
      showResult,
    }),

    [
      isComplete,
      setComplete,
      context,
      setValue,
      retake,
      goToResult,
      showResult,
    ],
  );

  return (
    <AnswerContext.Provider value={value}>{children}</AnswerContext.Provider>
  );
};

export const useAnswerContext = () => useContext(AnswerContext);
